<script setup lang="ts">
import { useElementBounding, useElementSize, useNow } from "@vueuse/core";
import { storeToRefs } from "pinia";
import { computed, useTemplateRef } from "vue";

import { DATA_TEST_IDS } from "@/constants/data-test-ids/data-test-ids.constant";
import { useTypedIntl } from "@/locales/typed-intl";
import { useProgrammingPlanningStore } from "@/stores/piloting/programming-planning-store";
import $date, { EDateScaling } from "@/utils/date";
import { pad } from "@/utils/number";

const props = defineProps<{
  xPlotlineHeight: number;
}>();

const { t, k } = useTypedIntl();
const programmingPlanningStore = useProgrammingPlanningStore();
const { planningRangeScale, startDate, endDate } = storeToRefs(programmingPlanningStore);
const now = useNow({ interval: 60_000 });

const container = useTemplateRef("container");
const { width: containerWidth, height: containerHeight } = useElementBounding(container);
const todayTag = useTemplateRef("todayTag");
const { width: todayTagWidth, height: todayTagHeight } = useElementSize(
  todayTag,
  { width: 0, height: 0 },
  { box: "border-box" },
);

const todayMilliseconds = computed<number>(() => $date.getDateMilliseconds(now.value));

const startDateMilliseconds = computed<number>(() => $date.getDateMilliseconds(startDate.value));
const endDateMilliseconds = computed<number>(() => $date.getDateMilliseconds(endDate.value));

const isTodayIncludedInDateRange = computed<boolean>(() => {
  return todayMilliseconds.value >= startDateMilliseconds.value && todayMilliseconds.value <= endDateMilliseconds.value;
});

const todayPlotLineTranslateX = computed<number>(() => {
  if (!isTodayIncludedInDateRange.value) return 0;

  const filterDateRangeMilliseconds = endDateMilliseconds.value - startDateMilliseconds.value;
  const todayMillisecondsDiff = todayMilliseconds.value - startDateMilliseconds.value;

  const proportion = todayMillisecondsDiff / filterDateRangeMilliseconds;

  return proportion * containerWidth.value;
});

const todayTagTranslateX = computed<number>(() => {
  return todayPlotLineTranslateX.value - todayTagWidth.value / 2;
});

const todayTagTranslateY = computed<number>(() => {
  return containerHeight.value / 2 - todayTagHeight.value / 2;
});
</script>

<template>
  <div
    ref="container"
    class="flex w-full relative h-[50px] bg-gray-50 overflow-visible"
    :data-test-id="DATA_TEST_IDS.components.planningTodayHeaderCell.component"
  >
    <template v-if="isTodayIncludedInDateRange">
      <div
        ref="todayTag"
        class="bg-red-600 rounded-xl h-[23px] min-w-[83px] flex items-center justify-center p-2 absolute"
        :data-test-id="DATA_TEST_IDS.components.planningTodayHeaderCell.todayTag"
        :style="{ translate: `${todayTagTranslateX}px ${todayTagTranslateY}px` }"
      >
        <small class="text-white font-light">
          <template v-if="planningRangeScale === EDateScaling.DAY"
            >{{ pad(`${now.getHours()}`) }}: {{ pad(`${now.getMinutes()}`) }}</template
          >
          <template v-else>{{ t(k.global.misc.today) }}</template>
        </small>
      </div>
      <div
        class="!w-[1px] z-20 bg-red-600/30 block absolute"
        :data-test-id="DATA_TEST_IDS.components.planningTodayHeaderCell.todayPlotLine"
        :style="{ translate: `${todayPlotLineTranslateX}px`, height: `${props.xPlotlineHeight}px` }"
      />
    </template>
  </div>
</template>
